import { useState, useEffect, useRef } from "react";
import { BUILD_VERSION } from "@/main";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { Truck, Eye, EyeOff, ChevronLeft } from "lucide-react";
import { MFAVerification } from "@/components/MFAVerification";

import { NativeAppLayout, useIsNativeApp } from "@/components/NativeAppLayout";
import { logActivityDirect } from "@/hooks/useActivityLog";
import { SUBSCRIPTION_TIERS, SubscriptionTierKey } from "@/lib/subscriptionTiers";
import { getStoredUtm, clearStoredUtm } from "@/lib/utmTracking";
import { trackFunnelEvent } from "@/lib/funnelTracking";
import { Capacitor } from "@capacitor/core";
import { productionUrl } from "@/lib/productionUrls";
import { isSafeInternalPath } from "@/lib/safeRedirect";

import wordmark from "@/assets/transportmate-wordmark.png";
import { getRecoverableWorkflow } from "@/lib/activeWorkflowGuard";

/** 1.72: a valid active workflow owned by this user beats /dashboard. */
const signedInFallbackRoute = (user: { id?: string; email?: string } | null | undefined, superAdminEmail: string) => {
  if (user?.email === superAdminEmail) return "/sa/dashboard";
  return getRecoverableWorkflow(user?.id)?.route ?? "/dashboard";
};
import blueLogo from "@/assets/transportmate-logo-blue-transparent.png";
import {
  isBiometricAvailable,
  isBiometricOptedIn,
  authenticateWithBiometric,
  saveCredentials,
  getBiometricTypeLabel,
  consumeBiometricAutoLoginSuppressed,
} from "@/lib/biometricAuth";

import { Fingerprint } from "lucide-react";
import { AppFooter } from "@/components/AppFooter";

const SUPER_ADMIN_EMAIL = "aaron@transportmate.co.uk"; // Goes to subscriptions page
const ADMIN_EMAIL = "ajleaney@me.com"; // Goes to admin dashboard

// Validation schemas
const loginSchema = z.object({
  email: z.string()
    .trim()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  password: z.string()
    .min(6, "Password must be at least 6 characters")
    .max(100, "Password must be less than 100 characters"),
  rememberMe: z.boolean().default(true),
});

// Signup collects only what account creation genuinely needs. Phone, company
// address and postcode are NOT required here: provisioning inserts safe
// placeholders (handle-new-user) and the details are collected in Settings
// before invoicing. This keeps the trial-start form short.
const signupSchema = z.object({
  name: z.string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
  company_name: z.string()
    .trim()
    .min(2, "Company name must be at least 2 characters")
    .max(200, "Company name must be less than 200 characters"),
  email: z.string()
    .trim()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters")
    .refine((email) => {
      // Ensure email has a valid domain with at least 2 characters after the last dot
      const parts = email.split('@');
      if (parts.length !== 2) return false;
      const domain = parts[1];
      const domainParts = domain.split('.');
      return domainParts.length >= 2 && domainParts[domainParts.length - 1].length >= 2;
    }, "Please use a valid email domain (e.g., @gmail.com, @yahoo.co.uk)")
    .refine((email) => {
      // Block reserved test domains (RFC 2606) and common disposable/throwaway email providers.
      // These are almost exclusively used by bots, fake signups, or temporary accounts that
      // never complete payment — they create noise in the user base and waste trial slots.
      const domain = email.split('@')[1]?.toLowerCase() ?? '';
      const blockedDomains = new Set([
        // RFC 2606 reserved domains
        'example.com', 'example.net', 'example.org',
        'test.com', 'test.net', 'test.org',
        'invalid', 'localhost',
        // Common disposable / throwaway providers
        'mailinator.com', 'guerrillamail.com', 'guerrillamail.net', 'guerrillamail.org',
        'sharklasers.com', 'grr.la', 'guerrillamailblock.com',
        'tempmail.com', 'temp-mail.org', 'tempmail.net', 'tempmailo.com',
        '10minutemail.com', '10minutemail.net', '20minutemail.com',
        'trashmail.com', 'trashmail.net', 'throwawaymail.com',
        'yopmail.com', 'yopmail.fr', 'yopmail.net',
        'getnada.com', 'nada.email',
        'maildrop.cc', 'mailnesia.com', 'dispostable.com', 'fakeinbox.com',
        'mintemail.com', 'mohmal.com', 'spambox.us', 'spam4.me',
        'inboxbear.com', 'tempinbox.com', 'tempr.email', 'discard.email',
        'mytemp.email', 'mailcatch.com', 'mailnull.com', 'emailondeck.com',
        'fakemail.net', 'tempemail.net', 'tempmailaddress.com',
      ]);
      return !blockedDomains.has(domain);
    }, "Please use a valid business or personal email address. Disposable, test, and throwaway email domains are not permitted."),
  password: z.string()
    .min(6, "Password must be at least 6 characters")
    .max(100, "Password must be less than 100 characters")
    .regex(/[A-Za-z]/, "Password must contain at least one letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
});

type LoginFormData = z.infer<typeof loginSchema>;
type SignupFormData = z.infer<typeof signupSchema>;

// Password strength calculation
const calculatePasswordStrength = (password: string): { strength: number; label: string; color: string } => {
  if (!password) return { strength: 0, label: "", color: "" };
  
  let strength = 0;
  
  // Length check
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 15;
  
  // Character variety checks
  if (/[a-z]/.test(password)) strength += 15; // lowercase
  if (/[A-Z]/.test(password)) strength += 15; // uppercase
  if (/[0-9]/.test(password)) strength += 15; // numbers
  if (/[^A-Za-z0-9]/.test(password)) strength += 15; // special characters
  
  // Determine label and color
  if (strength < 40) {
    return { strength, label: "Weak", color: "bg-destructive" };
  } else if (strength < 70) {
    return { strength, label: "Medium", color: "bg-yellow-500" };
  } else {
    return { strength, label: "Strong", color: "bg-green-500" };
  }
};

const checkUserSubscription = async (userId: string, userEmail: string) => {
  // Super admin and admin always have access
  if (userEmail === SUPER_ADMIN_EMAIL || userEmail === ADMIN_EMAIL) {
    console.log("[Auth.tsx] Owner/Admin access granted");
    return { hasAccess: true, reason: "owner" };
  }

  // Fetch own subscription AND company membership in PARALLEL
  const [ownSubResult, companyMemberResult] = await Promise.all([
    supabase
      .from("user_subscriptions")
      .select("subscription_status, is_free_access, subscription_renewal_date")
      .eq("user_id", userId)
      .maybeSingle(),
    supabase
      .from("company_members")
      .select("company_id, is_owner")
      .eq("user_id", userId)
      .order("is_owner", { ascending: false })
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle(),
  ]);

  const ownSubscription = ownSubResult.data;
  console.log("[Auth.tsx] Own subscription check:", { userId, userEmail, ownSubscription });

  // Check if user has their own valid subscription
  if (ownSubscription) {
    const hasValidOwnSubscription = checkSubscriptionValidity(ownSubscription);
    if (hasValidOwnSubscription.hasAccess) {
      return hasValidOwnSubscription;
    }
  }

  // If no own subscription, check company owner's subscription
  const companyMember = companyMemberResult.data;
  console.log("[Auth.tsx] Company membership:", companyMember);

  if (companyMember?.company_id) {
    // Find the company owner
    const { data: companyOwner } = await supabase
      .from("company_members")
      .select("user_id")
      .eq("company_id", companyMember.company_id)
      .eq("is_owner", true)
      .maybeSingle();

    if (companyOwner?.user_id) {
      const { data: ownerSubscription } = await supabase
        .from("user_subscriptions")
        .select("subscription_status, is_free_access, subscription_renewal_date")
        .eq("user_id", companyOwner.user_id)
        .maybeSingle();

      if (ownerSubscription) {
        const hasValidOwnerSubscription = checkSubscriptionValidity(ownerSubscription);
        if (hasValidOwnerSubscription.hasAccess) {
          console.log("[Auth.tsx] Access granted via company owner subscription");
          return hasValidOwnerSubscription;
        }
      }
    }
  }

  console.log("[Auth.tsx] No valid subscription found");
  return { hasAccess: false, reason: "no_subscription" };
};

// Helper function to check subscription validity
const checkSubscriptionValidity = (subscription: {
  subscription_status: string;
  is_free_access: boolean;
  subscription_renewal_date: string | null;
}) => {
  if (subscription.is_free_access) {
    console.log("[Auth.tsx] Free access granted");
    return { hasAccess: true, reason: "free_access" };
  }

  if (subscription.subscription_status === "active" || subscription.subscription_status === "trialing" || subscription.subscription_status === "free") {
    console.log("[Auth.tsx] Active/trialing/free subscription granted:", subscription.subscription_status);
    return { hasAccess: true, reason: "active_subscription" };
  }

  // Check if subscription is cancelled but still in paid period
  if (subscription.subscription_status === "cancelled" && subscription.subscription_renewal_date) {
    const renewalDate = new Date(subscription.subscription_renewal_date);
    const now = new Date();
    
    if (renewalDate > now) {
      console.log("[Auth.tsx] Cancelled but still in paid period");
      return { hasAccess: true, reason: "cancelled_with_access" };
    }
  }

  console.log("[Auth.tsx] Invalid subscription status:", subscription.subscription_status);
  return { hasAccess: false, reason: subscription.subscription_status };
};

const Auth = () => {
  const searchParams = new URLSearchParams(window.location.search);

  // Strip ?debug from /auth URL to prevent loops
  if (searchParams.has('debug') && window.location.pathname.startsWith('/auth')) {
    searchParams.delete('debug');
    const cleanUrl = window.location.pathname + (searchParams.toString() ? '?' + searchParams.toString() : '') + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }

  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'signup');
  const isLoginRef = useRef(isLogin);
  useEffect(() => { isLoginRef.current = isLogin; }, [isLogin]);
  const [showMFAVerification, setShowMFAVerification] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [sessionCheckFailed, setSessionCheckFailed] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ strength: 0, label: "", color: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLabel, setBiometricLabel] = useState("Biometrics");
  const [showBiometricOptIn, setShowBiometricOptIn] = useState(false);
  const pendingBiometricCredsRef = useRef<{ email: string; password: string } | null>(null);
  const pendingTargetRouteRef = useRef<string>("/dashboard");
  const loginHandoffRef = useRef(false);
  const isNativeApp = useIsNativeApp();

  // Android hardware back: switch from signup → login instead of exiting
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return;

    let listenerHandle: { remove: () => void } | null = null;

    import('@capacitor/app').then(({ App }) => {
      App.addListener('backButton', () => {
        // Only intercept when on signup mode
        if (!isLoginRef.current) {
          setIsLogin(true);
        }
        // When on login mode, let the global guard handle it
      }).then((handle) => {
        listenerHandle = handle;
      });
    });

    return () => {
      listenerHandle?.remove();
    };
  }, []);

  // Check biometric availability on native
  useEffect(() => {
    if (!isNativeApp) return;
    let cancelled = false;

    (async () => {
      const available = await isBiometricAvailable();
      if (cancelled) return;
      setBiometricAvailable(available);
      if (available) {
        const label = await getBiometricTypeLabel();
        if (!cancelled) setBiometricLabel(label);
      }
    })();

    return () => { cancelled = true; };
  }, [isNativeApp]);

  // Auto-attempt biometric login on mount (only once)
  const biometricAttemptedRef = useRef(false);
  const biometricAutoSuppressedRef = useRef(false);

  useEffect(() => {
    if (!isNativeApp) return;
    biometricAutoSuppressedRef.current = consumeBiometricAutoLoginSuppressed();
    if (biometricAutoSuppressedRef.current) {
      console.log("[Auth] Skipping auto biometric login after manual logout");
    }
  }, [isNativeApp]);

  useEffect(() => {
    if (!isNativeApp || biometricAttemptedRef.current) return;
    if (biometricAutoSuppressedRef.current) return;
    if (!biometricAvailable) return;
    if (!isBiometricOptedIn()) return;
    biometricAttemptedRef.current = true;

    (async () => {
      const creds = await authenticateWithBiometric();
      if (creds) {
        console.log("[Auth] Biometric auto-login attempt");
        setLoading(true);
        try {
          const { data: authData, error } = await supabase.auth.signInWithPassword({
            email: creds.email,
            password: creds.password,
          });
          if (error) {
            console.warn("[Auth] Biometric auto-login failed:", error.message);
            setLoading(false);
            return;
          }
          await completeLogin(authData, true);
        } catch (e) {
          console.error("[Auth] Biometric auto-login error:", e);
          setLoading(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biometricAvailable, isNativeApp]);

  const selectedTier = (searchParams.get('tier') as SubscriptionTierKey) || 'starter';
  const tierInfo = SUBSCRIPTION_TIERS[selectedTier] || SUBSCRIPTION_TIERS.starter;
  const navigate = useNavigate();
  const { status: authStatus, session } = useAuth();
  const { toast } = useToast();

  // Form configurations
  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: true,
    },
  });

  const signupForm = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      company_name: "",
      email: "",
      password: "",
    },
  });

  // Funnel: one anonymous signup_view per tab session when the signup form shows.
  useEffect(() => {
    if (!isLogin) trackFunnelEvent("signup_view");
  }, [isLogin]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('setup') === 'complete') {
      toast({
        title: "Setup Complete!",
        description: "Your trial subscription is active. Please log in to access your dashboard.",
        duration: 8000,
      });
      window.history.replaceState({}, '', '/auth');
    }
    if (searchParams.get('exited') === 'demo') {
      toast({
        title: "You've exited the demo",
        description: "The demo company and its sample data are no longer available on this device. Sign in or start your free trial whenever you're ready.",
        duration: 10000,
      });
      window.history.replaceState({}, '', '/auth');
    }
  }, [toast]);


  // ── Redirect if user lands on /auth while already authenticated ──
  // This handles: app resume with valid session, deep link to /auth, etc.
  // Post-login navigation is handled directly in completeLogin, NOT here.
  useEffect(() => {
    if (authStatus === "loading") {
      // Safety: if auth stays loading for 5s, show login form anyway
      const sessionTimeout = setTimeout(() => {
        console.warn("[Auth] AuthContext still loading after 5s — showing login form");
        setSessionCheckFailed(true);
      }, 5000);
      return () => clearTimeout(sessionTimeout);
    }

    setSessionCheckFailed(false);

    // If already authenticated and we didn't just complete a login (loginHandoffRef
    // handles that via direct navigation in completeLogin), redirect away from /auth
    if (authStatus === "authed" && session && !loginHandoffRef.current) {
      const urlParams = new URLSearchParams(window.location.search);
      const redirectPath = urlParams.get("redirect");
      const inviteToken = sessionStorage.getItem("invite_token");
      const fallbackRoute = signedInFallbackRoute(session.user, SUPER_ADMIN_EMAIL);
      const targetRoute =
        isSafeInternalPath(redirectPath)
          ? redirectPath
          : inviteToken
            ? `/accept-invite?token=${inviteToken}`
            : fallbackRoute;


      console.log("[Auth] Already authenticated — redirecting to", targetRoute);
      navigate(targetRoute, { replace: true });
    }
  }, [authStatus, session, navigate]);

  const completeLogin = async (authData: any, rememberMe: boolean) => {
    try {
      // Store remember me preference
      if (!rememberMe) {
        window.addEventListener('beforeunload', async () => {
          await supabase.auth.signOut();
        });
      }

      // Check for invite redirect first, then default route
      const urlParams = new URLSearchParams(window.location.search);
      const redirectPath = urlParams.get("redirect");
      const inviteToken = sessionStorage.getItem("invite_token");
      
      let targetRoute: string;
      if (isSafeInternalPath(redirectPath)) {
        targetRoute = redirectPath;

      } else if (inviteToken) {
        targetRoute = `/accept-invite?token=${inviteToken}`;
      } else {
        targetRoute = signedInFallbackRoute(authData.user, SUPER_ADMIN_EMAIL);
      }

      // Store biometric credentials BEFORE navigating so sessionStorage is ready
      if (isNativeApp && !isBiometricOptedIn() && pendingBiometricCredsRef.current) {
        try {
          sessionStorage.setItem("tm_pending_biometric_creds", JSON.stringify(pendingBiometricCredsRef.current));
          sessionStorage.setItem("tm_pending_biometric_prompt", "true");
          console.log("[Auth] Stored biometric creds for post-navigation prompt");
        } catch (e) {
          console.warn("[Auth] Failed to store biometric creds:", e);
        }
      }

      toast({
        title: "Welcome back!",
        description: "You've successfully logged in.",
      });

      // ── FIX: Navigate DIRECTLY instead of relying on useEffect ──
      // The previous approach waited for AuthContext's useEffect to detect the
      // auth state change, but React state batching in native WebView caused
      // the useEffect to fire inconsistently, resulting in a white screen.
      // Now we navigate immediately — signInWithPassword has already set the
      // session in the Supabase client, so ProtectedRoute will see it.
      console.log("[Auth] Login successful — navigating directly to", targetRoute);
      loginHandoffRef.current = true;
      navigate(targetRoute, { replace: true });

      // ── Background tasks (fire-and-forget, NO competing navigation) ──
      // Subscription gating is handled by ProtectedRoute — do NOT navigate
      // to /subscribe here, as it races with the navigation above.
      if (authData.user) {
        Promise.allSettled([
          supabase.rpc('update_user_last_login', { user_uuid: authData.user.id }),
          supabase.from("company_members")
            .select("company_id")
            .eq("user_id", authData.user.id)
            .order("is_owner", { ascending: false })
            .order("created_at", { ascending: true })
            .limit(1)
            .maybeSingle()
            .then(({ data: memberData }) => 
              logActivityDirect({
                userId: authData.user.id,
                companyId: memberData?.company_id,
                actionType: "user_login",
                description: `User logged in: ${authData.user.email}`,
                metadata: { email: authData.user.email },
              })
            ),
        ]).catch(err => console.error("[Auth] Background task error:", err));
      }
    } catch (error: any) {
      console.error("[Auth] completeLogin error:", error);
      loginHandoffRef.current = false;
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  const handleLogin = async (data: LoginFormData) => {
    console.log("[Auth] handleLogin called");
    setLoading(true);

    // Safety timeout: if login takes longer than 30s, reset loading state
    const safetyTimeout = setTimeout(() => {
      console.error("[Auth] Login safety timeout reached (30s)");
      setLoading(false);
      toast({
        title: "Login Taking Too Long",
        description: "Please try again. If the problem persists, tap 'Reset Login' below.",
        variant: "destructive",
      });
    }, 30000);

    try {
      console.log("[Auth] Calling signInWithPassword...");
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      if (error) {
        console.log("[Auth] signInWithPassword error:", error.message);
        if (error.message?.toLowerCase().includes('mfa')) {
          toast({
            title: "MFA Required",
            description: "Please complete two-factor authentication.",
          });
          setShowMFAVerification(true);
          setPendingLoginData({ email: data.email, password: data.password, rememberMe: data.rememberMe });
          setLoading(false);
          clearTimeout(safetyTimeout);
          return;
        }

        toast({
          title: "Login Failed",
          description: error.message,
          variant: "destructive",
        });
        setLoading(false);
        clearTimeout(safetyTimeout);
        return;
      }

      console.log("[Auth] signInWithPassword success — navigating immediately");
      clearTimeout(safetyTimeout);

      // Store biometric creds for opt-in prompt on dashboard
      if (isNativeApp && !isBiometricOptedIn()) {
        pendingBiometricCredsRef.current = { email: data.email, password: data.password };
      }

      // Navigate IMMEDIATELY — no MFA check blocking navigation
      // MFA is checked fire-and-forget; if needed, user gets redirected back
      await completeLogin(authData, data.rememberMe);

      // Fire-and-forget MFA check AFTER navigation has started
      supabase.auth.mfa.listFactors().then(({ data: factors }) => {
        const hasMFA = factors?.totp?.some(factor => factor.status === 'verified') ?? false;
        if (hasMFA) {
          console.log("[Auth] MFA required — redirecting back for verification");
          setPendingLoginData({ ...authData, rememberMe: data.rememberMe });
          setShowMFAVerification(true);
        }
      }).catch(e => console.warn("[Auth] MFA check failed:", e));

    } catch (error: any) {
      console.error("[Auth] handleLogin catch:", error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      if (!loginHandoffRef.current) {
        setLoading(false);
      }
      clearTimeout(safetyTimeout);
    }
  };

  const handleMFASuccess = async () => {
    if (pendingLoginData) {
      setShowMFAVerification(false);
      await completeLogin(pendingLoginData, pendingLoginData.rememberMe || true);
    }
  };

  const handleMFACancel = async () => {
    setShowMFAVerification(false);
    setPendingLoginData(null);
    await supabase.auth.signOut();
    toast({
      title: "Login Cancelled",
      description: "Please try again.",
    });
  };

  const handleResetLogin = () => {
    // Clear only Supabase auth storage keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.startsWith('supabase.'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
    console.log("[Auth] Reset login: cleared", keysToRemove.length, "auth keys");
    window.location.replace('/auth');
  };


  const handleSignup = async (data: SignupFormData) => {
    setLoading(true);
    trackFunnelEvent("signup_submitted");

    try {
      // Step 1: Create the user account
      const utm = getStoredUtm();
      const { data: authData, error: signupError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: productionUrl("/dashboard"),
          data: {
            name: data.name,
            company_name: data.company_name,
            ...(utm ? { utm } : {}),
          },
        },
      });

      if (signupError) throw signupError;

      if (!authData.user) {
        throw new Error("Failed to create user account");
      }

      const trialMessage = selectedTier === 'starter' 
        ? "Redirecting you to complete your subscription setup with a 30-day free trial..."
        : `Redirecting you to complete your ${tierInfo.name} plan subscription...`;
      
      toast({
        title: "Account created!",
        description: trialMessage,
        duration: 5000,
      });
      clearStoredUtm();

      // Step 2: Wait a moment for the webhook to process
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Check if this is an invite-based signup
      const inviteToken = sessionStorage.getItem("invite_token");
      const urlParams = new URLSearchParams(window.location.search);
      const redirectParam = urlParams.get("redirect");
      const isInviteSignup = !!inviteToken || (redirectParam && redirectParam.includes("accept-invite"));

      // On iOS native: skip Stripe checkout — ProtectedRoute will show the IAP paywall
      const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
      if (isIOS) {
        toast({
          title: "Account created!",
          description: "Please sign in to choose your subscription plan.",
          duration: 5000,
        });
        setLoading(false);
        setIsLogin(true);
        return;
      }

      // Step 3: Create Stripe checkout session with the selected tier
      // Try create-trial-checkout first, fall back to create-checkout
      let checkoutUrl: string | null = null;
      
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke(
        'create-trial-checkout',
        { 
          body: { 
            tierKey: selectedTier,
            priceId: tierInfo.priceId,
            ...(isInviteSignup && inviteToken ? { inviteToken } : {}),
          } 
        }
      );

      if (checkoutError || !checkoutData?.url) {
        console.warn("create-trial-checkout failed, trying fallback:", checkoutError?.message);
        const { data: fallbackData, error: fallbackError } = await supabase.functions.invoke(
          'create-checkout',
          {
            body: {
              tierKey: selectedTier,
              priceId: tierInfo.priceId,
              ...(isInviteSignup && inviteToken ? { inviteToken } : {}),
            }
          }
        );
        if (fallbackError) {
          console.error("Fallback checkout also failed:", fallbackError);
          throw new Error("Failed to create checkout session. Please try again or contact support.");
        }
        checkoutUrl = fallbackData?.url;
        
        // Non-blocking: trigger health check to alert admin
        supabase.functions.invoke("signup-health-check", {}).catch(() => {});
      } else {
        checkoutUrl = checkoutData.url;
      }

      if (!checkoutUrl) {
        throw new Error("No checkout URL received");
      }

      // Step 4: Redirect to Stripe checkout
      // After successful payment, user will be redirected back to login
      // Use openExternalUrl to prevent WebView navigation on native Android
      const { openExternalUrl } = await import("@/lib/openExternalUrl");
      await openExternalUrl(checkoutUrl);
    } catch (error: any) {
      console.error("Signup error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to complete signup. Please try again.",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  if (showMFAVerification) {
    return (
      <div className={`min-h-screen flex flex-col p-4 ${isNativeApp ? 'bg-background' : 'bg-gradient-to-br from-secondary via-background to-secondary/50'}`}>
        <div className="flex flex-1 items-center justify-center">
          <MFAVerification 
            onSuccess={handleMFASuccess}
            onCancel={handleMFACancel}
          />
        </div>
        <AppFooter className="mt-8 pb-[max(1.5rem,env(safe-area-inset-bottom))]" />
      </div>
    );
  }

  const authContent = (
    <div className={`min-h-screen flex flex-col ${isNativeApp ? 'bg-white' : 'bg-gradient-to-br from-secondary via-background to-secondary/50'}`}>
      {/* Location permission is now handled by LocationReminderBanner */}

      {/* Native Header — White background + Blue logo + Divider */}
      {isNativeApp && (
        <div className="flex-shrink-0 bg-white" style={{ backgroundColor: '#ffffff' }}>
          <div style={{ paddingTop: "env(safe-area-inset-top, 0px)", backgroundColor: '#ffffff' }} />
          <div className="relative flex items-center justify-center px-6 pb-3 pt-3">
            {/* Back button — only shown on Create Account */}
            {!isLogin && (
              <button
                type="button"
                onClick={() => setIsLogin(true)}
                className="absolute left-4 flex items-center justify-center w-10 h-10 rounded-full active:bg-muted transition-colors"
                aria-label="Back to login"
              >
                <ChevronLeft className="w-6 h-6 text-primary" />
              </button>
            )}
            {/* Blue logo */}
            {logoFailed ? (
              <span className="text-xl font-bold text-primary tracking-tight">TransportMate</span>
            ) : (
              <img
                src={blueLogo}
                alt="TransportMate"
                className="h-10 w-auto object-contain"
                onError={() => setLogoFailed(true)}
              />
            )}
          </div>
          {/* Subtle divider */}
          <div className="h-px w-full bg-border" />
        </div>
      )}

      {/* Web: Back to Home button */}
      {!isNativeApp && (
        <div className="absolute top-4 left-4">
          <Button variant="ghost" onClick={() => navigate("/")}>
            ← Back to Home
          </Button>
        </div>
      )}

      {/* Main Content Container.
           Native ALWAYS renders the single-column mobile layout: the content
           column is capped at phone width and the card fills it edge to edge,
           so large-screen native devices (iPad / Android tablets) can never
           fall through to the wide web presentation.
           Web uses a two-column split on desktop (branding + form) collapsing
           to a single centred column below the lg breakpoint. */}
      <div
        className={
          isNativeApp
            ? "flex-1 flex flex-col items-stretch justify-start mx-auto w-full max-w-[430px] px-4 pt-3"
            : "flex-1 grid grid-rows-1 lg:grid-cols-[1.05fr_1fr] min-h-0"
        }
      >
        {/* Desktop branding panel — hidden on native and below lg */}
        <aside
          className={
            isNativeApp
              ? "hidden"
              : "hidden lg:flex relative flex-col justify-center overflow-hidden px-12 xl:px-20 py-12 bg-gradient-to-br from-primary/10 via-secondary/40 to-background"
          }
        >
          <div className="relative z-10 max-w-md space-y-6">
            <div className="flex items-center gap-3">
              <span className="h-11 w-11 rounded-xl bg-primary flex items-center justify-center">
                <Truck className="h-6 w-6 text-primary-foreground" />
              </span>
              <span className="font-bold text-xl tracking-tight">TransportMate</span>
            </div>
            <h2 className="text-3xl font-bold leading-tight tracking-tight">
              Vehicle transport management, made simple.
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              Appraisals, live tracking, proof of delivery and invoicing in one
              platform built for UK logistics operators.
            </p>
            <ul className="space-y-2.5 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><span className="text-primary">●</span> Paperless appraisal & damage capture</li>
              <li className="flex items-center gap-2"><span className="text-primary">●</span> Real-time driver tracking</li>
              <li className="flex items-center gap-2"><span className="text-primary">●</span> Automated invoicing & accounting sync</li>
            </ul>
          </div>
        </aside>
        {/* Right form column (web) / transparent pass-through (native) */}
        <div
          className={
            isNativeApp
              ? "contents"
              : "flex items-center justify-center p-4 sm:p-6 lg:p-10"
          }
        >
        <div className={isNativeApp ? "contents" : "w-full max-w-[500px]"}>

        <Card className={`w-full ${isNativeApp ? 'max-w-none border-0 shadow-none bg-transparent' : 'bg-card/80 backdrop-blur border-border/50'}`}>

          <CardHeader className={isNativeApp ? "space-y-3 pt-2 pb-4" : "space-y-4"}>
            {!isNativeApp && (
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
                  <Truck className="w-8 h-8 text-primary-foreground" />
                </div>
              </div>
            )}
            <div className="text-center space-y-2">
              <CardTitle className={`font-bold ${isNativeApp ? 'text-2xl' : 'text-3xl'}`}>
                {isLogin ? "Welcome Back" : "Create Account"}
              </CardTitle>
              <CardDescription className="text-base">
                {isLogin
                  ? "Enter your credentials to access your account"
                  : "Fill in your details to get started"}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
          {isLogin ? (
            <Form {...loginForm}>
              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                <FormField
                  control={loginForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input placeholder="you@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={loginForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input 
                            type={showPassword ? "text" : "password"} 
                            placeholder="••••••••" 
                            {...field} 
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                    )}
                  </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={loginForm.control}
                  name="rememberMe"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="text-sm font-normal cursor-pointer">
                        Remember me
                      </FormLabel>
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Please wait..." : "Log In"}
                </Button>
                {/* Biometric login button — only on native with opted-in */}
                {isNativeApp && biometricAvailable && isBiometricOptedIn() && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full gap-2"
                    disabled={loading}
                    onClick={async () => {
                      const creds = await authenticateWithBiometric();
                      if (creds) {
                        setLoading(true);
                        try {
                          const { data: authData, error } = await supabase.auth.signInWithPassword({
                            email: creds.email,
                            password: creds.password,
                          });
                          if (error) {
                            toast({ title: "Login Failed", description: error.message, variant: "destructive" });
                            setLoading(false);
                            return;
                          }
                          await completeLogin(authData, true);
                        } catch (e: any) {
                          toast({ title: "Error", description: e.message, variant: "destructive" });
                          setLoading(false);
                        }
                      }
                    }}
                  >
                    <Fingerprint className="h-4 w-4" />
                    Log in with {biometricLabel}
                  </Button>
                )}
                {/* Recovery UI: show if session check failed or login timed out */}
                {(sessionCheckFailed || (!loading && loginForm.formState.submitCount > 0)) && (
                  <div className="mt-3 p-3 rounded-lg border border-destructive/30 bg-destructive/5 space-y-2">
                    <p className="text-xs text-muted-foreground text-center">
                      Having trouble connecting?
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => window.location.reload()}
                      >
                        Retry
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="flex-1"
                        onClick={handleResetLogin}
                      >
                        Reset Login
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </Form>
          ) : (
            <Form {...signupForm} key="signup-form">
              <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
                <FormField
                  control={signupForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="John Doe" 
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={signupForm.control}
                  name="company_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Your Company Name"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                
                
                <FormField
                  control={signupForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="you@example.com" 
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={signupForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input 
                            type={showPassword ? "text" : "password"} 
                            placeholder="••••••••" 
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              setPasswordStrength(calculatePasswordStrength(e.target.value));
                            }}
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      {field.value && (
                        <div className="space-y-2 mt-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Password strength:</span>
                            <span className={`font-medium ${
                              passwordStrength.label === "Weak" ? "text-destructive" :
                              passwordStrength.label === "Medium" ? "text-yellow-500" :
                              "text-green-500"
                            }`}>
                              {passwordStrength.label}
                            </span>
                          </div>
                          <Progress 
                            value={passwordStrength.strength} 
                            className="h-2"
                            indicatorClassName={passwordStrength.color}
                          />
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Please wait..." : "Create Account"}
                </Button>
              </form>
            </Form>
          )}
          <div className="mt-6 text-center space-y-2">
            {isLogin && (
              <button
                type="button"
                onClick={() => navigate("/reset-password")}
                className="text-sm text-muted-foreground hover:text-primary transition-colors block w-full"
              >
                Forgot your password?
              </button>
            )}
            {/* iOS native: show "View Plans" instead of "Create account" */}
            {Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios' ? (
              isLogin ? (
                <button
                  type="button"
                  onClick={() => navigate("/ios-plans")}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  New to TransportMate?{" "}
                  <span className="font-medium text-primary">View Plans</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { setIsLogin(true); signupForm.reset(); }}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Already have an account?{" "}
                  <span className="font-medium text-primary">Log in</span>
                </button>
              )
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (isLogin) {
                    navigate("/subscribe");
                  } else {
                    setIsLogin(true);
                    signupForm.reset();
                  }
                }}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <span className="font-medium text-primary">
                  {isLogin ? "Pick a plan" : "Log in"}
                </span>
              </button>
            )}
          </div>
        </CardContent>
      </Card>
      {(import.meta.env.DEV || new URLSearchParams(window.location.search).has('debug')) && (
        <div className="text-center mt-6 space-y-0.5">
          <p className="text-[10px] text-muted-foreground/60">Build: {BUILD_VERSION}</p>
          <p className="text-[10px] text-muted-foreground/60">Platform: {isNativeApp ? 'native' : 'web'} | {window.location.hostname}</p>
        </div>
      )}
        {isNativeApp && (
          <AppFooter className="mt-8 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:mt-10" />
        )}
        </div>
        </div>
      </div>
      {!isNativeApp && (
        <AppFooter className="pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4" />
      )}
    </div>
  );

  return (
    <>
      {authContent}
      <AlertDialog open={showBiometricOptIn} onOpenChange={setShowBiometricOptIn}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5" />
              Enable {biometricLabel}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to use {biometricLabel} to log in next time? Your credentials will be stored securely on this device.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowBiometricOptIn(false);
              pendingBiometricCredsRef.current = null;
              navigate(pendingTargetRouteRef.current);
            }}>
              No thanks
            </AlertDialogCancel>
            <AlertDialogAction onClick={async () => {
              const creds = pendingBiometricCredsRef.current;
              if (creds) {
                const saved = await saveCredentials(creds.email, creds.password);
                if (saved) {
                  toast({
                    title: `${biometricLabel} Enabled`,
                    description: `You can now use ${biometricLabel} to log in next time.`,
                  });
                }
              }
              pendingBiometricCredsRef.current = null;
              setShowBiometricOptIn(false);
              navigate(pendingTargetRouteRef.current);
            }}>
              Enable {biometricLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Auth;
